import type { PublishableAccessDecisionContract } from '@automatick/shared-ng';

export const PublishableViewerRouteFamilies = {
  AuthenticatedViewer: 'AuthenticatedViewer',
  UnlistedTokenViewer: 'UnlistedTokenViewer',
  PublicSlugViewer: 'PublicSlugViewer',
  ManagementSurface: 'ManagementSurface',
} as const;

export type PublishableViewerRouteFamilies =
  (typeof PublishableViewerRouteFamilies)[keyof typeof PublishableViewerRouteFamilies];

export const PublishableViewerRouteDataKeys = {
  RouteFamily: 'publishableRouteFamily',
  RouteParameterName: 'publishableRouteParameterName',
} as const;

export const PublishableViewerRouteParameters = {
  PublishableId: 'publishableId',
  Token: 'token',
  Slug: 'slug',
} as const;

export const PublishablePermissionResultCodes = {
  Allowed: 'Allowed',
  Unauthorized: 'Unauthorized',
  RequiresAuthentication: 'RequiresAuthentication',
  SourceNotFound: 'SourceNotFound',
  SourceAccessDenied: 'SourceAccessDenied',
  WorkspacePolicyBlocked: 'WorkspacePolicyBlocked',
  SourceSensitivityBlocked: 'SourceSensitivityBlocked',
  LifecycleBlocked: 'LifecycleBlocked',
  AccessModeBlocked: 'AccessModeBlocked',
  SnapshotModeBlocked: 'SnapshotModeBlocked',
  TokenExpired: 'TokenExpired',
  TokenRevoked: 'TokenRevoked',
  NotImplemented: 'NotImplemented',
  UnsupportedSourceKind: 'UnsupportedSourceKind',
} as const;

export const PublishableLifecycleStates = {
  Draft: 'Draft',
  Private: 'Private',
  Shared: 'Shared',
  Published: 'Published',
  Suspended: 'Suspended',
  Revoked: 'Revoked',
  Expired: 'Expired',
  Deleted: 'Deleted',
} as const;

export const PublishableAccessModes = {
  PrivateOwner: 'PrivateOwner',
  PrivateWorkspace: 'PrivateWorkspace',
  RequiresSourceConversationAccess: 'RequiresSourceConversationAccess',
  UnlistedLink: 'UnlistedLink',
  RestrictedRecipients: 'RestrictedRecipients',
  Public: 'Public',
  Expired: 'Expired',
  Revoked: 'Revoked',
} as const;

export const PublishableActionKinds = {
  Open: 'Open',
  OpenStandalone: 'OpenStandalone',
  CreatePrivate: 'CreatePrivate',
  CreateShareLink: 'CreateShareLink',
  CopyShareLink: 'CopyShareLink',
  RevokeShareLink: 'RevokeShareLink',
  PublishPublic: 'PublishPublic',
  CopyPublicRoute: 'CopyPublicRoute',
  UnpublishPublic: 'UnpublishPublic',
  Manage: 'Manage',
  ViewSource: 'ViewSource',
  DownloadArtifact: 'DownloadArtifact',
} as const;

export const PublishableActionPlacements = {
  Inline: 'Inline',
  DetailPrimary: 'DetailPrimary',
  DetailOverflow: 'DetailOverflow',
  ConversationEllipsis: 'ConversationEllipsis',
  ManagementToolbar: 'ManagementToolbar',
  ManagementRow: 'ManagementRow',
  ViewerToolbar: 'ViewerToolbar',
} as const;

export type PublishableActionPlacements =
  (typeof PublishableActionPlacements)[keyof typeof PublishableActionPlacements];

export const PublishableRiskTiers = {
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
} as const;

export const PublishableViewerLocalizationKeys = {
  ViewerTitle: 'publishables.viewer.title',
  SharesAndPublications: 'publishables.menu.sharesAndPublications',
  ManagementTitle: 'publishables.management.title',
  ManagementSubtitle: 'publishables.management.subtitle',
  ManagementLoading: 'publishables.management.loading',
  ManagementEmptyTitle: 'publishables.management.empty.title',
  ManagementEmptyBody: 'publishables.management.empty.body',
  ManagementErrorTitle: 'publishables.management.error.title',
  ManagementErrorBody: 'publishables.management.error.body',
  ManagementCount: 'publishables.management.count',
  ManagementActionUnavailable: 'publishables.management.action.unavailable',
  ManagementActionInProgress: 'publishables.management.action.inProgress',
  ManagementShareLinkCopied: 'publishables.management.action.shareLinkCopied',
  ManagementPublicLinkCopied: 'publishables.management.action.publicLinkCopied',
  ManagementActionCompleted: 'publishables.management.action.completed',
  ManagementActionFailed: 'publishables.management.action.failed',
  FilterState: 'publishables.management.filter.state',
  FilterAccess: 'publishables.management.filter.access',
  FilterAllStates: 'publishables.management.filter.state.all',
  FilterAllAccess: 'publishables.management.filter.access.all',
  AccessPrivateOwner: 'publishables.access.privateOwner',
  AccessPrivateWorkspace: 'publishables.access.privateWorkspace',
  AccessRequiresSourceConversationAccess: 'publishables.access.requiresSourceConversationAccess',
  AccessUnlistedLink: 'publishables.access.unlistedLink',
  AccessRestrictedRecipients: 'publishables.access.restrictedRecipients',
  AccessPublic: 'publishables.access.public',
  AccessExpired: 'publishables.access.expired',
  AccessRevoked: 'publishables.access.revoked',
  ColumnTitle: 'publishables.management.column.title',
  ColumnState: 'publishables.management.column.state',
  ColumnAccess: 'publishables.management.column.access',
  ColumnUpdated: 'publishables.management.column.updated',
  AuthenticatedViewerTitle: 'publishables.viewer.route.authenticated',
  UnlistedViewerTitle: 'publishables.viewer.route.unlisted',
  PublicViewerTitle: 'publishables.viewer.route.public',
  Loading: 'publishables.viewer.loading',
  EmptyTitle: 'publishables.viewer.empty.title',
  EmptyBody: 'publishables.viewer.empty.body',
  ErrorTitle: 'publishables.viewer.error.title',
  ErrorBody: 'publishables.viewer.error.body',
  DeniedTitle: 'publishables.viewer.denied.title',
  ArtifactSectionTitle: 'publishables.viewer.artifacts.title',
  OpenArtifact: 'publishables.viewer.artifacts.open',
  UnknownArtifact: 'publishables.viewer.artifacts.unknown',
  StateDraft: 'publishables.state.draft',
  StatePrivate: 'publishables.state.private',
  StateShared: 'publishables.state.shared',
  StatePublished: 'publishables.state.published',
  StateSuspended: 'publishables.state.suspended',
  StateRevoked: 'publishables.state.revoked',
  StateExpired: 'publishables.state.expired',
  StateDeleted: 'publishables.state.deleted',
  DeniedUnauthorized: 'publishables.denied.unauthorized',
  DeniedSourceAccess: 'publishables.denied.sourceAccess',
  DeniedWorkspacePolicy: 'publishables.denied.workspacePolicy',
  DeniedTokenExpired: 'publishables.denied.tokenExpired',
  DeniedTokenRevoked: 'publishables.denied.tokenRevoked',
  DeniedUnsupported: 'publishables.denied.unsupported',
  DeniedNotFound: 'publishables.denied.notFound',
  DeniedLifecycle: 'publishables.denied.lifecycle',
  ActionOpen: 'publishables.action.open',
  ActionOpenStandalone: 'publishables.action.openStandalone',
  ActionCreatePrivate: 'publishables.action.createPrivate',
  ActionCreateShareLink: 'publishables.action.createShareLink',
  ActionCopyShareLink: 'publishables.action.copyShareLink',
  ActionRevokeShareLink: 'publishables.action.revokeShareLink',
  ActionPublishPublic: 'publishables.action.publishPublic',
  ActionCopyPublicRoute: 'publishables.action.copyPublicRoute',
  ActionUnpublishPublic: 'publishables.action.unpublishPublic',
  ActionManage: 'publishables.action.manage',
  ActionViewSource: 'publishables.action.viewSource',
  ActionDownloadArtifact: 'publishables.action.downloadArtifact',
} as const;

export const PublishableViewerTranslations: Readonly<Record<string, string>> = {
  [PublishableViewerLocalizationKeys.ViewerTitle]: 'Publishable',
  [PublishableViewerLocalizationKeys.SharesAndPublications]: 'Shares and publications',
  [PublishableViewerLocalizationKeys.ManagementTitle]: 'Shares and publications',
  [PublishableViewerLocalizationKeys.ManagementSubtitle]: 'Conversation publishables',
  [PublishableViewerLocalizationKeys.ManagementLoading]: 'Loading publishables',
  [PublishableViewerLocalizationKeys.ManagementEmptyTitle]: 'No shares or publications yet',
  [PublishableViewerLocalizationKeys.ManagementEmptyBody]: 'Publishables created from this conversation will appear here.',
  [PublishableViewerLocalizationKeys.ManagementErrorTitle]: 'Unable to load publishables',
  [PublishableViewerLocalizationKeys.ManagementErrorBody]: 'Shares and publications could not be loaded for this conversation.',
  [PublishableViewerLocalizationKeys.ManagementCount]: 'items',
  [PublishableViewerLocalizationKeys.ManagementActionUnavailable]: 'This action is not available for the current state.',
  [PublishableViewerLocalizationKeys.ManagementActionInProgress]: 'Working',
  [PublishableViewerLocalizationKeys.ManagementShareLinkCopied]: 'Share link copied',
  [PublishableViewerLocalizationKeys.ManagementPublicLinkCopied]: 'Public link copied',
  [PublishableViewerLocalizationKeys.ManagementActionCompleted]: 'Updated',
  [PublishableViewerLocalizationKeys.ManagementActionFailed]: 'Action failed',
  [PublishableViewerLocalizationKeys.FilterState]: 'State',
  [PublishableViewerLocalizationKeys.FilterAccess]: 'Access',
  [PublishableViewerLocalizationKeys.FilterAllStates]: 'All states',
  [PublishableViewerLocalizationKeys.FilterAllAccess]: 'All access',
  [PublishableViewerLocalizationKeys.AccessPrivateOwner]: 'Private owner',
  [PublishableViewerLocalizationKeys.AccessPrivateWorkspace]: 'Private workspace',
  [PublishableViewerLocalizationKeys.AccessRequiresSourceConversationAccess]: 'Source conversation',
  [PublishableViewerLocalizationKeys.AccessUnlistedLink]: 'Share link',
  [PublishableViewerLocalizationKeys.AccessRestrictedRecipients]: 'Restricted recipients',
  [PublishableViewerLocalizationKeys.AccessPublic]: 'Public',
  [PublishableViewerLocalizationKeys.AccessExpired]: 'Expired',
  [PublishableViewerLocalizationKeys.AccessRevoked]: 'Revoked',
  [PublishableViewerLocalizationKeys.ColumnTitle]: 'Title',
  [PublishableViewerLocalizationKeys.ColumnState]: 'State',
  [PublishableViewerLocalizationKeys.ColumnAccess]: 'Access',
  [PublishableViewerLocalizationKeys.ColumnUpdated]: 'Updated',
  [PublishableViewerLocalizationKeys.AuthenticatedViewerTitle]: 'Private publishable',
  [PublishableViewerLocalizationKeys.UnlistedViewerTitle]: 'Shared publishable',
  [PublishableViewerLocalizationKeys.PublicViewerTitle]: 'Public publishable',
  [PublishableViewerLocalizationKeys.Loading]: 'Loading publishable',
  [PublishableViewerLocalizationKeys.EmptyTitle]: 'No presentation is available',
  [PublishableViewerLocalizationKeys.EmptyBody]: 'This publishable loaded, but it does not include displayable content yet.',
  [PublishableViewerLocalizationKeys.ErrorTitle]: 'Unable to load publishable',
  [PublishableViewerLocalizationKeys.ErrorBody]: 'The publishable could not be loaded from the current route.',
  [PublishableViewerLocalizationKeys.DeniedTitle]: 'This publishable is not available',
  [PublishableViewerLocalizationKeys.ArtifactSectionTitle]: 'Files',
  [PublishableViewerLocalizationKeys.OpenArtifact]: 'Open',
  [PublishableViewerLocalizationKeys.UnknownArtifact]: 'Untitled file',
  [PublishableViewerLocalizationKeys.StateDraft]: 'Draft',
  [PublishableViewerLocalizationKeys.StatePrivate]: 'Private',
  [PublishableViewerLocalizationKeys.StateShared]: 'Shared',
  [PublishableViewerLocalizationKeys.StatePublished]: 'Published',
  [PublishableViewerLocalizationKeys.StateSuspended]: 'Suspended',
  [PublishableViewerLocalizationKeys.StateRevoked]: 'Revoked',
  [PublishableViewerLocalizationKeys.StateExpired]: 'Expired',
  [PublishableViewerLocalizationKeys.StateDeleted]: 'Deleted',
  [PublishableViewerLocalizationKeys.DeniedUnauthorized]: 'Sign in to view this publishable.',
  [PublishableViewerLocalizationKeys.DeniedSourceAccess]: 'You do not have access to the source for this publishable.',
  [PublishableViewerLocalizationKeys.DeniedWorkspacePolicy]: 'Workspace policy blocks access to this publishable.',
  [PublishableViewerLocalizationKeys.DeniedTokenExpired]: 'This share link has expired.',
  [PublishableViewerLocalizationKeys.DeniedTokenRevoked]: 'This share link has been revoked.',
  [PublishableViewerLocalizationKeys.DeniedUnsupported]: 'This publishable source is not supported for viewing yet.',
  [PublishableViewerLocalizationKeys.DeniedNotFound]: 'This publishable could not be found.',
  [PublishableViewerLocalizationKeys.DeniedLifecycle]: 'This publishable is not currently viewable.',
  [PublishableViewerLocalizationKeys.ActionOpen]: 'Open',
  [PublishableViewerLocalizationKeys.ActionOpenStandalone]: 'Open',
  [PublishableViewerLocalizationKeys.ActionCreatePrivate]: 'Create private publishable',
  [PublishableViewerLocalizationKeys.ActionCreateShareLink]: 'Create share link',
  [PublishableViewerLocalizationKeys.ActionCopyShareLink]: 'Copy share link',
  [PublishableViewerLocalizationKeys.ActionRevokeShareLink]: 'Revoke share link',
  [PublishableViewerLocalizationKeys.ActionPublishPublic]: 'Publish',
  [PublishableViewerLocalizationKeys.ActionCopyPublicRoute]: 'Copy public link',
  [PublishableViewerLocalizationKeys.ActionUnpublishPublic]: 'Unpublish',
  [PublishableViewerLocalizationKeys.ActionManage]: 'Manage',
  [PublishableViewerLocalizationKeys.ActionViewSource]: 'View source',
  [PublishableViewerLocalizationKeys.ActionDownloadArtifact]: 'Download',
};

export const PublishableLifecycleLocalizationKeys: Readonly<Record<string, string>> = {
  [PublishableLifecycleStates.Draft]: PublishableViewerLocalizationKeys.StateDraft,
  [PublishableLifecycleStates.Private]: PublishableViewerLocalizationKeys.StatePrivate,
  [PublishableLifecycleStates.Shared]: PublishableViewerLocalizationKeys.StateShared,
  [PublishableLifecycleStates.Published]: PublishableViewerLocalizationKeys.StatePublished,
  [PublishableLifecycleStates.Suspended]: PublishableViewerLocalizationKeys.StateSuspended,
  [PublishableLifecycleStates.Revoked]: PublishableViewerLocalizationKeys.StateRevoked,
  [PublishableLifecycleStates.Expired]: PublishableViewerLocalizationKeys.StateExpired,
  [PublishableLifecycleStates.Deleted]: PublishableViewerLocalizationKeys.StateDeleted,
};

export const PublishableAccessModeLocalizationKeys: Readonly<Record<string, string>> = {
  [PublishableAccessModes.PrivateOwner]: PublishableViewerLocalizationKeys.AccessPrivateOwner,
  [PublishableAccessModes.PrivateWorkspace]: PublishableViewerLocalizationKeys.AccessPrivateWorkspace,
  [PublishableAccessModes.RequiresSourceConversationAccess]: PublishableViewerLocalizationKeys.AccessRequiresSourceConversationAccess,
  [PublishableAccessModes.UnlistedLink]: PublishableViewerLocalizationKeys.AccessUnlistedLink,
  [PublishableAccessModes.RestrictedRecipients]: PublishableViewerLocalizationKeys.AccessRestrictedRecipients,
  [PublishableAccessModes.Public]: PublishableViewerLocalizationKeys.AccessPublic,
  [PublishableAccessModes.Expired]: PublishableViewerLocalizationKeys.AccessExpired,
  [PublishableAccessModes.Revoked]: PublishableViewerLocalizationKeys.AccessRevoked,
};

export const PublishableDeniedLocalizationKeys: Readonly<Record<string, string>> = {
  [PublishablePermissionResultCodes.Unauthorized]: PublishableViewerLocalizationKeys.DeniedUnauthorized,
  [PublishablePermissionResultCodes.RequiresAuthentication]: PublishableViewerLocalizationKeys.DeniedUnauthorized,
  [PublishablePermissionResultCodes.SourceAccessDenied]: PublishableViewerLocalizationKeys.DeniedSourceAccess,
  [PublishablePermissionResultCodes.SourceNotFound]: PublishableViewerLocalizationKeys.DeniedNotFound,
  [PublishablePermissionResultCodes.WorkspacePolicyBlocked]: PublishableViewerLocalizationKeys.DeniedWorkspacePolicy,
  [PublishablePermissionResultCodes.SourceSensitivityBlocked]: PublishableViewerLocalizationKeys.DeniedWorkspacePolicy,
  [PublishablePermissionResultCodes.LifecycleBlocked]: PublishableViewerLocalizationKeys.DeniedLifecycle,
  [PublishablePermissionResultCodes.AccessModeBlocked]: PublishableViewerLocalizationKeys.DeniedLifecycle,
  [PublishablePermissionResultCodes.SnapshotModeBlocked]: PublishableViewerLocalizationKeys.DeniedLifecycle,
  [PublishablePermissionResultCodes.TokenExpired]: PublishableViewerLocalizationKeys.DeniedTokenExpired,
  [PublishablePermissionResultCodes.TokenRevoked]: PublishableViewerLocalizationKeys.DeniedTokenRevoked,
  [PublishablePermissionResultCodes.NotImplemented]: PublishableViewerLocalizationKeys.DeniedUnsupported,
  [PublishablePermissionResultCodes.UnsupportedSourceKind]: PublishableViewerLocalizationKeys.DeniedUnsupported,
};

export interface PublishableViewerRouteContext {
  readonly routeFamily: PublishableViewerRouteFamilies;
  readonly routeParameterName: string;
  readonly routeParameterValue: string;
}

export interface PublishableViewerDeniedState {
  readonly accessDecision: PublishableAccessDecisionContract | null;
  readonly messageLocalizationKey: string;
}

export interface PublishableActionViewModel {
  readonly actionKey: string;
  readonly actionKind: string;
  readonly placement: string;
  readonly label: string;
  readonly labelLocalizationKey: string | null;
  readonly priority: number;
  readonly enabled: boolean;
  readonly disabledReasonCode: string | null;
  readonly route: string | null;
  readonly riskTier: string | null;
  readonly confirmationRequired: boolean;
  readonly requiresFreshHydration: boolean;
}
